import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AccountTree as AccountTreeIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LockOutlined as LockOutlinedIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptLongIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon,
  Attachment as AttachmentIcon,
} from '@mui/icons-material';
import {
  PROGRAMA_CONTABILIDAD,
  esRolAdministrador,
  puedeEditarProgramaContabilidad,
  puedeVerModuloContabilidad,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import DefinicionFiscal from './DefinicionFiscal';
import CatalogoAnexos from './CatalogoAnexos';
import CatalogoDeclaraciones from './CatalogoDeclaraciones';

const PROGRAMA = 'catalogodecuentas';

const TIPOS_CUENTA = [
  'Activo Corriente',
  'Activo Fijo',
  'Pasivo Corriente',
  'Pasivo No Corriente',
  'Capital',
  'Ingreso',
  'Compra de Mercancia',
  'Materia Prima',
  'Mano de Obra',
  'Costos de Fabricacion',
  'Costos Generales',
  'Gasto Financiero',
  'Gasto General',
  'Gasto Operacional',
];

const ORIGENES = ['Debito', 'Credito'];

const GRUPOS_CUENTA = ['Activo', 'Pasivo', 'Capital', 'Ingresos', 'Costos', 'Gastos'];

const labelDefinicionFiscal = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d;
  const doc = String(d.documento || '').trim();
  const desc = String(d.descripcion || '').trim();
  if (doc && desc) return `${doc} — ${desc}`;
  return doc || desc || (d.id != null ? String(d.id) : '');
};

const valoresVacios = {
  cuenta: '',
  nombre: '',
  tipo: '',
  grupo: '',
  origen: '',
  nivel: '',
  maestra: '',
  clase: '',
  cuentapresup: '',
  iddefinicionfiscal: null,
};

const CatalogoCuentasContables = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles, roleWeb } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer =
    puedeVerModuloContabilidad(roleDetalles, roleWeb) ||
    puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA);
  const puedeModificar =
    esRolAdministrador(roleWeb) || puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA);
  const puedeEliminar = puedeModificar;

  const [cuentas, setCuentas] = useState([]);
  const [definicionesFiscales, setDefinicionesFiscales] = useState([]);
  const [guardandoDefinicionId, setGuardandoDefinicionId] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState('cuenta');
  const [formulario, setFormulario] = useState(valoresVacios);
  const [editandoId, setEditandoId] = useState(null);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [dialogoFiscalAbierto, setDialogoFiscalAbierto] = useState(false);
  const [dialogoAnexosAbierto, setDialogoAnexosAbierto] = useState(false);
  const [dialogoDeclaracionesAbierto, setDialogoDeclaracionesAbierto] = useState(false);

  const puedeOperarDb = Boolean(selectedCompany?.db_empresa);

  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  const btnPrimarySx = {
    height: 38,
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    borderRadius: 1.4,
    textTransform: 'none',
    fontWeight: 700,
    boxShadow: 'none',
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
  };

  const btnOutlinedSx = {
    height: 38,
    borderRadius: 1.4,
    textTransform: 'none',
    fontWeight: 700,
    borderColor: FP.color.border,
    color: FP.color.navy,
    '&:hover': { borderColor: FP.color.navy, bgcolor: FP.color.surfaceAlt },
  };

  const cargarCatalogo = useCallback(async () => {
    if (!puedeOperarDb) return;
    setCargando(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarcatalogo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          busqueda,
          orden,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        showError(data.Error || 'No se pudo cargar el catálogo');
        return;
      }
      setCuentas(data.cuentas || []);
    } catch (e) {
      showError('Error de conexión al cargar el catálogo');
    } finally {
      setCargando(false);
    }
  }, [puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, busqueda, orden, showError]);

  const cargarDefinicionesFiscales = useCallback(async () => {
    if (!puedeOperarDb) {
      setDefinicionesFiscales([]);
      return;
    }
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listardefinicionfiscal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setDefinicionesFiscales([]);
        return;
      }
      setDefinicionesFiscales(Array.isArray(data.definiciones) ? data.definiciones : []);
    } catch (e) {
      setDefinicionesFiscales([]);
    }
  }, [puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) {
      cargarCatalogo();
      cargarDefinicionesFiscales();
    }
    // Solo al entrar o cambiar de empresa; la búsqueda se aplica con «Consultar»
  }, [puedeLeer, puedeOperarDb, selectedCompany?.db_empresa]);

  const actualizarCampo = (campo, valor) => {
    setFormulario((prev) => ({ ...prev, [campo]: valor }));
  };

  /** Solo dígitos para el código de cuenta (alta). */
  const normalizarCodigoCuenta = (valor) => String(valor ?? '').replace(/\D/g, '');

  const rellenarFormularioDesdeFila = (fila) => {
    setEditandoId(fila.id);
    setFormulario({
      cuenta: fila.cuenta ?? '',
      nombre: fila.nombre ?? '',
      tipo: fila.tipo ?? '',
      grupo: fila.grupo ?? '',
      origen: fila.origen ?? '',
      nivel: fila.nivel != null ? fila.nivel : '',
      maestra: fila.maestra ?? '',
      clase: fila.clase ?? '',
      cuentapresup: fila.cuentapresup ?? '',
      iddefinicionfiscal: fila.iddefinicionfiscal != null && fila.iddefinicionfiscal !== ''
        ? fila.iddefinicionfiscal
        : null,
    });
  };

  const abrirDialogoNuevaCuenta = () => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar el catálogo');
      return;
    }
    setEditandoId(null);
    setFormulario(valoresVacios);
    cargarDefinicionesFiscales();
    setDialogoAbierto(true);
  };

  const abrirDialogoEditar = (fila) => {
    rellenarFormularioDesdeFila(fila);
    cargarDefinicionesFiscales();
    setDialogoAbierto(true);
  };

  const cerrarDialogo = () => {
    setDialogoAbierto(false);
    setEditandoId(null);
    setFormulario(valoresVacios);
  };

  const guardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar el catálogo');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!puedeOperarDb) {
      showError('Seleccione una empresa con base de datos');
      return;
    }
    const cuenta = (formulario.cuenta || '').trim();
    const nombre = (formulario.nombre || '').trim();
    if (!cuenta || !nombre) {
      showError('Código de cuenta y nombre son obligatorios');
      return;
    }

    if (!editandoId) {
      const tipo = (formulario.tipo || '').trim();
      const grupo = (formulario.grupo || '').trim();
      const origen = (formulario.origen || '').trim();
      if (!tipo) {
        showError('Debe indicar el tipo de cuenta');
        return;
      }
      if (!grupo) {
        showError('Debe indicar el grupo de la cuenta');
        return;
      }
      if (!origen) {
        showError('Debe indicar el origen (Débito o Crédito)');
        return;
      }
    }

    const cuerpoBase = {
      id_empresa: selectedCompany,
      cuenta,
      nombre,
      tipo: (formulario.tipo || '').trim(),
      grupo: (formulario.grupo || '').trim(),
      origen: (formulario.origen || '').trim(),
      nivel: formulario.nivel === '' || formulario.nivel == null ? '' : formulario.nivel,
      iddefinicionfiscal: formulario.iddefinicionfiscal == null || formulario.iddefinicionfiscal === ''
        ? null
        : formulario.iddefinicionfiscal,
      maestra: (formulario.maestra || '').trim(),
      clase: (formulario.clase || '').trim(),
      cuentapresup: (formulario.cuentapresup || '').trim(),
      user: usuarioLog || '',
      usuario: usuarioLog || '',
      password: claveLog || '',
    };

    try {
      if (editandoId) {
        const response = await fetch(`${servidor}/contabilidadbackend_actualizar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...cuerpoBase, id: editandoId }),
        });
        const data = await response.json();
        if (!response.ok) {
          showError(data.Error || 'No se pudo actualizar');
          return;
        }
        showSuccess(data.mensaje || 'Cuenta actualizada');
      } else {
        const response = await fetch(`${servidor}/contabilidadbackend_crear`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cuerpoBase),
        });
        const data = await response.json();
        if (!response.ok) {
          showError(data.Error || 'No se pudo crear la cuenta');
          return;
        }
        showSuccess(data.mensaje || 'Cuenta creada');
      }
      await cargarCatalogo();
      cerrarDialogo();
    } catch (e) {
      showError('Error de conexión al guardar');
    }
  };

  const eliminar = async (id, cuenta) => {
    const cuentaid = cuenta.trim();

    if (!puedeEliminar) {
      showError('No tiene permiso para eliminar cuentas');
      return;
    }
    if (!puedeOperarDb) return;
    if (!window.confirm('¿Eliminar esta cuenta del catálogo?')) return;

    try {
      const response = await fetch(`${servidor}/contabilidadbackend_eliminar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          id,
          cuentaid,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        showError(data.Error || 'No se pudo eliminar');
        return;
      }
      showSuccess(data.mensaje || 'Cuenta eliminada');
      if (dialogoAbierto && editandoId === id) {
        cerrarDialogo();
      }
      await cargarCatalogo();
    } catch (e) {
      showError('Error de conexión al eliminar');
    }
  };

  const guardarDefinicionFiscalFila = async (row, definicion) => {
    if (!puedeModificar || !usuarioLog || !puedeOperarDb) {
      showError('No tiene permiso para modificar el catálogo');
      return;
    }
    const idDef = definicion?.id != null ? definicion.id : null;
    const actual = row.iddefinicionfiscal != null && row.iddefinicionfiscal !== ''
      ? Number(row.iddefinicionfiscal)
      : null;
    const siguiente = idDef != null ? Number(idDef) : null;
    if (actual === siguiente) return;

    setGuardandoDefinicionId(row.id);
    setCuentas((prev) =>
      prev.map((c) => (c.id === row.id ? { ...c, iddefinicionfiscal: siguiente } : c))
    );
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_actualizar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          id: row.id,
          iddefinicionfiscal: siguiente,
          user: usuarioLog || '',
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        showError(data.Error || 'No se pudo guardar la definición fiscal');
        await cargarCatalogo();
        return;
      }
    } catch (e) {
      showError('Error de conexión al guardar la definición fiscal');
      await cargarCatalogo();
    } finally {
      setGuardandoDefinicionId(null);
    }
  };

  const imprimirListado = () => {
    const titulo = `Catálogo de cuentas — ${selectedCompany?.name || nombreNegocio || ''}`;
    const filas = (cuentas || [])
      .map(
        (c) =>
          `<tr><td>${escapeHtml(String(c.cuenta ?? ''))}</td><td>${escapeHtml(
            String(c.nombre ?? '')
          )}</td><td>${escapeHtml(String(c.tipo ?? ''))}</td><td>${escapeHtml(
            String(c.grupo ?? '')
          )}</td><td>${escapeHtml(String(c.origen ?? ''))}</td><td>${escapeHtml(
            labelDefinicionFiscal(
              definicionesFiscales.find(
                (d) => String(d.id) === String(c.iddefinicionfiscal ?? '')
              ) || ''
            )
          )}</td><td>${escapeHtml(
            String(c.maestra ?? '')
          )}</td><td>${escapeHtml(String(c.clase ?? ''))}</td><td>${escapeHtml(
            String(c.cuentapresup ?? '')
          )}</td></tr>`
      )
      .join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${escapeHtml(
      titulo
    )}</title><style>
      body{font-family:Segoe UI,Arial,sans-serif;padding:24px;color:#111}
      h1{font-size:18px;margin-bottom:16px}
      table{border-collapse:collapse;width:100%;font-size:12px}
      th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
      th{background:#f0f0f0}
    </style></head><body><h1>${escapeHtml(titulo)}</h1>
    <p style="font-size:12px;color:#444">${new Date().toLocaleString()}</p>
    <table><thead><tr>
      <th>Cuenta</th><th>Nombre</th><th>Tipo</th><th>Grupo</th><th>Origen</th><th>Definición Fiscal</th><th>Maestra</th><th>Clase</th><th>Cuenta presup.</th>
    </tr></thead><tbody>${filas}</tbody></table>
    </body></html>`;
    const w = window.open('', '_blank');
    if (!w) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    setTimeout(() => {
      w.focus();
      w.print();
    }, 250);
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Catálogo de cuentas contables"
          subtitle={
            nombreNegocio
              ? `${nombreNegocio} · Plan de cuentas`
              : 'Plan de cuentas'
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «catalogodecuentas» en roles para administrar el catálogo.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeOperarDb) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Catálogo de cuentas contables"
          subtitle={
            nombreNegocio
              ? `${nombreNegocio} · Plan de cuentas`
              : 'Plan de cuentas'
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
            }}
          >
            Seleccione una empresa con base de datos asignada para administrar el catálogo contable.
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Catálogo de cuentas contables"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Plan de cuentas`
            : 'Plan de cuentas'
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccountTreeIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros y acciones
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={abrirDialogoNuevaCuenta}
                disabled={!puedeModificar || !usuarioLog}
                sx={btnPrimarySx}
              >
                Añadir cuenta
              </Button>
              <Button
                variant="outlined"
                startIcon={<ReceiptLongIcon />}
                onClick={() => setDialogoFiscalAbierto(true)}
                disabled={!puedeLeer}
                sx={btnOutlinedSx}
              >
                Definición Fiscal
              </Button>
              <Button
                variant="outlined"
                startIcon={<AttachmentIcon />}
                onClick={() => setDialogoAnexosAbierto(true)}
                disabled={!puedeLeer}
                sx={btnOutlinedSx}
              >
                Anexos
              </Button>
              <Button
                variant="outlined"
                startIcon={<AssignmentIcon />}
                onClick={() => setDialogoDeclaracionesAbierto(true)}
                disabled={!puedeLeer}
                sx={btnOutlinedSx}
              >
                Declaraciones
              </Button>
              <TextField
                size="small"
                label="Buscar"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && cargarCatalogo()}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180, ...fpInputSx }}
              />
              <TextField
                select
                size="small"
                label="Orden"
                value={orden}
                onChange={(e) => setOrden(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 140, ...fpInputSx }}
              >
                <MenuItem value="cuenta">Cuenta</MenuItem>
                <MenuItem value="nombre">Nombre</MenuItem>
                <MenuItem value="nivel">Nivel</MenuItem>
                <MenuItem value="tipo">Tipo</MenuItem>
              </TextField>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={cargarCatalogo}
                disabled={cargando}
                sx={btnOutlinedSx}
              >
                Consultar
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={imprimirListado}
                sx={btnOutlinedSx}
              >
                Imprimir
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Listado de cuentas
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}>
              {cargando ? 'Cargando…' : `${cuentas.length} registro(s)`}
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
            >
              <Table size="small" sx={{ minWidth: 1240 }}>
                <TableBody>
                  <TableRow
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      '& td': {
                        border: 'none',
                        borderBottom: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Cuenta
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Nombre
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Tipo
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Grupo
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Origen
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 260 }}>
                      <Typography component="span" sx={cellHeadSx}>
                        Definición Fiscal
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Maestra
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Clase
                      </Typography>
                    </TableCell>
                    <TableCell align="right" className="no-print">
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                        Acciones
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {cuentas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6, color: FP.color.textSecondary }}>
                        {cargando ? 'Cargando…' : 'No hay cuentas para mostrar.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    cuentas.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        selected={dialogoAbierto && editandoId === row.id}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell onClick={() => abrirDialogoEditar(row)}>{row.cuenta}</TableCell>
                        <TableCell onClick={() => abrirDialogoEditar(row)}>{row.nombre}</TableCell>
                        <TableCell onClick={() => abrirDialogoEditar(row)}>{row.tipo}</TableCell>
                        <TableCell onClick={() => abrirDialogoEditar(row)}>{row.grupo}</TableCell>
                        <TableCell onClick={() => abrirDialogoEditar(row)}>{row.origen}</TableCell>
                        <TableCell
                          sx={{ minWidth: 260, maxWidth: 360 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Autocomplete
                            options={definicionesFiscales}
                            value={
                              definicionesFiscales.find(
                                (d) => String(d.id) === String(row.iddefinicionfiscal ?? '')
                              ) || null
                            }
                            onChange={(_, v) => guardarDefinicionFiscalFila(row, v)}
                            getOptionLabel={labelDefinicionFiscal}
                            isOptionEqualToValue={(a, b) => String(a?.id ?? '') === String(b?.id ?? '')}
                            disabled={!puedeModificar || !usuarioLog || guardandoDefinicionId === row.id}
                            size="small"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Definición fiscal"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                sx={fpInputSx}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell onClick={() => abrirDialogoEditar(row)}>{row.maestra}</TableCell>
                        <TableCell onClick={() => abrirDialogoEditar(row)}>{row.clase}</TableCell>
                        <TableCell align="right" className="no-print">
                          <IconButton
                            size="small"
                            onClick={() => abrirDialogoEditar(row)}
                            disabled={!puedeModificar || !usuarioLog}
                            title="Editar"
                            sx={{
                              color: FP.color.accentBlue,
                              '&:hover': { bgcolor: FP.color.accentBlueSoft },
                              '&.Mui-disabled': { color: FP.color.textMuted },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              eliminar(row.id, row.cuenta);
                            }}
                            disabled={!puedeEliminar || !usuarioLog}
                            title="Eliminar"
                            sx={{
                              color: FP.color.danger,
                              '&:hover': { bgcolor: FP.color.dangerSoft },
                              '&.Mui-disabled': { color: FP.color.textMuted },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={dialogoAbierto}
        onClose={cerrarDialogo}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AccountTreeIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            {editandoId ? 'Editar cuenta contable' : 'Añadir cuenta contable'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Código de cuenta"
                value={formulario.cuenta}
                onChange={(e) => actualizarCampo('cuenta', normalizarCodigoCuenta(e.target.value))}
                disabled={!puedeModificar || !!editandoId}
                autoFocus
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Nombre"
                value={formulario.nombre}
                onChange={(e) => actualizarCampo('nombre', e.target.value)}
                disabled={!puedeModificar}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Tipo"
                value={formulario.tipo}
                onChange={(e) => actualizarCampo('tipo', e.target.value)}
                disabled={!puedeModificar || !!editandoId}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              >
                <MenuItem value="">(ninguno)</MenuItem>
                {TIPOS_CUENTA.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Grupo"
                value={formulario.grupo}
                onChange={(e) => actualizarCampo('grupo', e.target.value)}
                disabled={!puedeModificar || !!editandoId}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              >
                <MenuItem value="">(ninguno)</MenuItem>
                {GRUPOS_CUENTA.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Origen"
                value={formulario.origen}
                onChange={(e) => actualizarCampo('origen', e.target.value)}
                disabled={!puedeModificar || !!editandoId}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              >
                <MenuItem value="">(ninguno)</MenuItem>
                {ORIGENES.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Autocomplete
                options={definicionesFiscales}
                value={
                  definicionesFiscales.find(
                    (d) => String(d.id) === String(formulario.iddefinicionfiscal ?? '')
                  ) || null
                }
                onChange={(_, v) => actualizarCampo('iddefinicionfiscal', v?.id ?? null)}
                getOptionLabel={labelDefinicionFiscal}
                isOptionEqualToValue={(a, b) => String(a?.id ?? '') === String(b?.id ?? '')}
                disabled={!puedeModificar}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Definición Fiscal"
                    placeholder="Buscar definición fiscal"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={cerrarDialogo}
            startIcon={<CloseIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={guardar}
            disabled={!puedeModificar || !usuarioLog}
            sx={btnPrimarySx}
          >
            {editandoId ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <DefinicionFiscal
        open={dialogoFiscalAbierto}
        onClose={() => setDialogoFiscalAbierto(false)}
        puedeModificar={puedeModificar && Boolean(usuarioLog)}
        puedeEliminar={puedeEliminar && Boolean(usuarioLog)}
      />
      <CatalogoAnexos
        open={dialogoAnexosAbierto}
        onClose={() => setDialogoAnexosAbierto(false)}
        puedeModificar={puedeModificar && Boolean(usuarioLog)}
        puedeEliminar={puedeEliminar && Boolean(usuarioLog)}
      />
      <CatalogoDeclaraciones
        open={dialogoDeclaracionesAbierto}
        onClose={() => setDialogoDeclaracionesAbierto(false)}
        puedeModificar={puedeModificar && Boolean(usuarioLog)}
        puedeEliminar={puedeEliminar && Boolean(usuarioLog)}
      />
    </Box>
  );
};

function escapeHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export default CatalogoCuentasContables;
